import React, { useState, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
  debounce,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { StockLookupService, SymbolSearchResult } from '../services/stockLookupService';

interface SymbolLookupProps {
  value: string | null;
  onChange: (symbol: string | null, name?: string) => void;
  onStockSelected?: (stock: SymbolSearchResult) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
}

const SymbolLookup: React.FC<SymbolLookupProps> = ({
  value,
  onChange,
  onStockSelected,
  label = 'Stock Symbol',
  required = false,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  autoFocus = false,
}) => {
  const [options, setOptions] = useState<SymbolSearchResult[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Debounced search function
  const searchSymbols = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.trim().length < 1) {
          setOptions([]);
          return;
        }

        setLoading(true);
        setSearchError(null);

        try {
          const results = await StockLookupService.searchSymbols(query);
          setOptions(results);
        } catch (error: any) {
          console.error('Symbol search error:', error);
          setSearchError(error.message || 'Failed to search symbols');
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setInputValue(value);
      searchSymbols(value);
    },
    [searchSymbols]
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, value: SymbolSearchResult | string | null) => {
      if (typeof value === 'string') {
        // User typed a custom value
        onChange(value.toUpperCase());
      } else if (value) {
        // User selected from dropdown
        onChange(value.symbol, value.name);
        if (onStockSelected) {
          onStockSelected(value);
        }
      } else {
        onChange(null);
      }
    },
    [onChange, onStockSelected]
  );

  // Get display value for the selected option
  const selectedOption = useMemo(() => {
    if (!value) return null;
    
    // Try to find in current options
    const found = options.find(opt => opt.symbol === value);
    if (found) return found;
    
    // If not found, return just the symbol
    return value;
  }, [value, options]);

  return (
    <Autocomplete
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      freeSolo
      autoHighlight
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.symbol;
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" component="span" fontWeight="medium">
                {option.symbol}
              </Typography>
              <Chip 
                label={option.exchange} 
                size="small" 
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {option.name}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || !!searchError}
          helperText={helperText || searchError}
          autoFocus={autoFocus}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon color="action" sx={{ mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          placeholder="Search by symbol or company name"
        />
      )}
      noOptionsText={
        loading ? 'Searching...' : 
        inputValue.length < 1 ? 'Type to search' : 
        searchError ? 'Error searching symbols' :
        'No stocks found'
      }
      sx={{
        '& .MuiAutocomplete-option': {
          padding: 1.5,
        },
      }}
    />
  );
};

export default SymbolLookup;